import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TextsmsIcon from '@mui/icons-material/Textsms';
import { Box, Button, Tooltip, styled } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { ReactEventHandler } from 'react';
import { HiCursorClick, HiOfficeBuilding, HiOutlineArchive } from 'react-icons/hi';
import { IoBarcodeOutline } from 'react-icons/io5';
import { NavigateFunction } from 'react-router-dom';
import {
  ICandidate,
  ICandidateRecentConversation,
  IJob,
  IJobCandidateInteraction,
  ILocation,
  Interaction,
  amplitude,
  getOverlapTeams,
  IUserDTO,
  getJobByJobCode,
  getUserByEmail,
} from 'wisestep-api';
import {
  WsDetailsObj,
  WsIconLocation,
  WsIconTime,
  WsIconPosition,
  WsIconLikeLocation,
  WsIconProfile,
  WsIconDice,
  WsColors,
  WsToggleObject,
  IFilterList,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  IWorkspaceContext,
  formatDateToMonthDayYearString,
  WsIconBoard,
  WsIconCurrency,
  WsIconVisa,
  timeSince,
} from 'wisestep-base';

import { SubmissionAdvancedFilterFormProps } from '../../../components/filters';
import MappingsAdvanceFilterFormProps from '../../../components/filters/models/MappingAdvanceFiltersFormProps';
import TalentPoolAdvanceFilterFormProps from '../../../components/filters/models/TalentPoolAdvanceFiltersFormProps';
import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import { getRawPhoneNumber } from '../../../features/conversationWidget/utils/fieldDefaultValues';
import { IJobBoard } from '../../../features/jobs/publish/components/freeJobBoards/freeJobBoardCard/FreeJobBoardCard';
import CareerBuilder from '../../../images/jobBoardsIcons/CareerBuilder.png';
import cv from '../../../images/jobBoardsIcons/cv.png';
import CWJob from '../../../images/jobBoardsIcons/CWJob.png';
import Dice from '../../../images/jobBoardsIcons/Dice.png';
import JobisiteNew from '../../../images/jobBoardsIcons/JobisiteNew.png';
import JobServe from '../../../images/jobBoardsIcons/JobServe.png';
import MonsterIcon from '../../../images/jobBoardsIcons/Monster.png';
import TechFetchIcon from '../../../images/jobBoardsIcons/TechFetchIcon.jpeg';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { INotification } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { TalentPoolSearchRequest } from '../talentpool/components/search/TalentPoolSearchRequest';

export const apiFailedMessage =
  'There was an error in processing your search request. We have logged the error and our team will review it.Please try again with different search criteria';
export const Pipeline_Implementation_message =
  'We are working on providing new experience for Pipeline Search. Meanwhile please use Talentpool and JobSearch to find matching candidates. For any queries please reach out to our Customer Support Team.';
export const LayoutTheme = styled(Box)(() => ({
  maxWidth: '100%',
  maxHeight: 'xs',
  backgroundColor: WsColors.TertiaryShades50,
}));

export const AppLayoutTheme = styled(Box)(() => ({
  borderRadius: 8,
  backgroundColor: WsColors.TheBlues900,
}));

export enum CandidateType {
  SUBMISSIONS = 'submissions',
  TALENTPOOL = 'talentpool',
  PIPELINE = 'pipeline',
  SMART_SOURCE = 'smartSource',
}

export const copySuccess = () => success('Copied Successfully !!');
export const copyFailed = () => failed('Copy failed !! ');
export const copyWarn = () => warning('Clipboard not supported');

export const getCandidateDetails = (candidate: ICandidate, type: CandidateType): WsDetailsObj[] => {
  if (
    (candidate.source === 'LOCAL' ||
      candidate.source === 'CVUPLOAD_LOCAL' ||
      candidate.source === 'GDRIVE' ||
      candidate.source === 'CVUPLOAD_GDRIVE' ||
      candidate?.candidateStatusTags?.includes('applied')) &&
    (type === CandidateType.TALENTPOOL || type === CandidateType.PIPELINE)
  ) {
    return getParsedCandidateDetails(candidate, type);
  }
  const array: WsDetailsObj[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      labelName: getLocation(candidate.currentLocation) ?? 'Not Available',
      popOverText: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      labelName: `Not Available`,
      popOverText: 'Candidate Location',
    });
  }
  if (
    !isEmpty(candidate.companyName) &&
    (type === CandidateType.SUBMISSIONS || type === CandidateType.PIPELINE)
  ) {
    array.push({
      icon: <HiOfficeBuilding color={WsColors.TertiaryShades700} />,
      labelName: candidate.companyName ?? 'Not Available',
      popOverText: 'Client name',
    });
  }
  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      labelName: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      popOverText: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      labelName: candidate.previousJobTitles?.[0],
      popOverText: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      labelName: 'Not Available',
      popOverText: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience || candidate?.totalMonthsExperience) {
    array.push({
      icon: <WsIconPosition />,
      labelName:
        candidate?.totalYearsExperience && candidate.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } years Exp`
          : `${candidate.totalYearsExperience ?? 0}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } year Exp`,
      popOverText: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconPosition />,
      labelName: 'Not Available',
      popOverText: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    labelName:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `Not Available`
        : candidate.source ?? `Not Available`,
    popOverText: 'Source',
  });

  if (candidate.jobPreferencePermanent || candidate.jobPreferenceContract) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `Prefers ${candidate.jobPreferenceContract ? 'Contract' : 'Permanent'} `,
      popOverText: 'Preferred Job Type',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `Not Available`,
      popOverText: 'Preferred Job Type',
    });
  }

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    labelName: getPreferredLocation(candidate),
    popOverText: 'Preferred Locations',
  });

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    labelName: candidate?.relocationPreference ?? 'Not Available',
    popOverText: 'Relocation Preferences',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    labelName:
      candidate.expectedSalaryCurrency &&
      candidate.expectedBaseSalaryTimeSpan &&
      candidate.expectedBaseSalary
        ? `${candidate.expectedSalaryCurrency} ${String(candidate.expectedBaseSalary)} ${
            candidate.expectedBaseSalaryTimeSpan ?? ''
          }`
        : 'Not available',
    popOverText: 'Expected Salary',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    labelName:
      candidate.currentSalaryCurrency && candidate.currentSalaryTimeSpan && candidate.currentSalary
        ? `${candidate.currentSalaryCurrency} ${String(candidate.currentSalary)} 
        ${candidate.currentSalaryTimeSpan}`
        : 'Not available',
    popOverText: 'Current Salary',
  });

  if (candidate?.appliedOn) {
    array.push({
      icon: <HiCursorClick color={WsColors.TertiaryShades700} />,
      labelName:
        (candidate?.appliedOn && timeSince(new Date(candidate?.appliedOn).toString())) ??
        'Not Available',
      popOverText: 'Applied On',
    });
  }

  array.push({
    icon: <WsIconVisa />,
    labelName: `${
      candidate.workAuthorization && candidate.workAuthorization?.length > 0
        ? candidate.workAuthorization
        : candidate.workAuthorizationCategory && candidate.workAuthorizationCategory.length > 0
        ? candidate.workAuthorizationCategory
        : 'Not Available'
    }`,
    popOverText: 'Visa Details',
  });

  if (candidate.noticePeriodInDays) {
    array.push({
      icon: <WsIconBoard />,
      labelName: `${candidate.noticePeriodInDays} Days`,
      popOverText: 'Notice period',
    });
  } else {
    array.push({
      icon: <WsIconBoard />,
      labelName: `Not Available`,
      popOverText: 'Notice period',
    });
  }
  if (!isEmpty(candidate.jobClientRecruiterName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName: candidate.jobClientRecruiterName ?? 'Not Available',
      popOverText: 'Client SPOC',
    });
  }
  if (!isEmpty(candidate.jobAccountManagerName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName: candidate.jobAccountManagerName ?? `Not Available`,
      popOverText: 'Account manager',
    });
  }
  if (!isEmpty(candidate.jobLocations) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconLocation />,
      labelName: getLocation(candidate?.jobLocations?.[0] as ILocation) ?? `Not Available`,
      popOverText: 'Job Location',
    });
  }
  return array;
};

const getParsedCandidateDetails = (candidate: ICandidate, type: CandidateType): WsDetailsObj[] => {
  const array: WsDetailsObj[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      labelName: getLocation(candidate.currentLocation) ?? 'Not Available',
      popOverText: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      labelName: `Not Available`,
      popOverText: 'Candidate Location',
    });
  }

  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      labelName: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      popOverText: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      labelName: candidate.previousJobTitles?.[0],
      popOverText: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      labelName: 'Not Available',
      popOverText: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience) {
    array.push({
      icon: <WsIconTime />,
      labelName:
        candidate?.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience} years Exp`
          : `${candidate.totalYearsExperience} year Exp`,
      popOverText: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      labelName: `Not Available`,
      popOverText: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    labelName:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `Not Available`
        : candidate.source ?? `Not Available`,
    popOverText: 'Source',
  });

  if (candidate.educationQualifications && candidate.educationQualifications?.length > 0) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `${candidate?.educationQualifications[0]} `,
      popOverText: 'Education Qualifications',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `Not Available`,
      popOverText: 'Education Qualifications',
    });
  }

  if (candidate.education) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `${candidate?.education[0]?.org} `,
      popOverText: 'Education Inst',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      labelName: `Not Available`,
      popOverText: 'Education Inst',
    });
  }

  if (candidate.previousJobTitles && candidate.previousJobTitles?.length > 0) {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      labelName: candidate.previousJobTitles[0],
      popOverText: 'Previous JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      labelName: `Not Available`,
      popOverText: 'Previous JobTitle',
    });
  }
  if (candidate.currentEmployer) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName: candidate.currentEmployer,
      popOverText: 'Current Employer',
    });
  } else {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName: `Not Available`,
      popOverText: 'Current Employer',
    });
  }

  if (candidate.gender === 'FEMALE' || candidate.gender === 'MALE') {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName: candidate.gender,
      popOverText: 'Gender',
    });
  }
  if (candidate.dob) {
    array.push({
      icon: <WsIconTime color={WsColors.TertiaryShades700} />,
      labelName: formatDateToMonthDayYearString(new Date(candidate?.dob)),
      popOverText: 'DOB',
    });
  }
  if (Array.isArray(candidate?.candidateLanguages) && candidate.candidateLanguages.length > 0) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      labelName:
        candidate.candidateLanguages
          .map((language) => language?.languageName ?? '')
          .filter((name) => name)
          .join(', ') ?? 'Not Available',
      popOverText: 'Languages Known',
    });
  }

  return array;
};
export interface CandidateCardProps {
  candidate: ICandidate;
  downloadSource?: string | null;
  job?: IJob | null;
  isSelected: boolean;
  checkBoxOnclick?: ReactEventHandler;
  candidateIndex?: number;
  onCVClick?: (args: number) => void;
  setDownloadSource?: (args: string, index: number) => void;
  onActionClick?: (args: number) => void;
  openSingleEmail: (candidate: ICandidate, email: string) => void;
  queryRefresh?: () => void;
  interaction?: Interaction | undefined;
  amplitudeEventHandler?: (eventType: string) => void;
  onOpenShareIcon?: (args: ICandidate) => void;
  isCvViewed?: boolean;
  isPipeline?: boolean;
  array?: string[];
  isSimijobs?: boolean;
  backwardpathArray?: IBreadCrumbsList[];
}

export const toggleArray: WsToggleObject[] = [
  {
    label: 'Current',
    value: 'Current',
  },
  {
    label: 'Preferred',
    value: 'Preferred',
  },
  {
    label: 'Current or Preferred',
    value: 'Current or Preferred',
  },
];

const addExpereinceTag = (data, filtersArray) => {
  if (data.totalYearsExperienceEndRange > 0 && data.experienceComparisonOperator === 'isBetween') {
    filtersArray.push({
      tag: `Expereince: ${data.totalYearsExperienceStartRange} - ${data.totalYearsExperienceEndRange} Years`,
      id: 'totalYearsExperienceStartRange',
    });
  } else if (
    data.totalYearsExperienceStartRange > 0 &&
    data.experienceComparisonOperator === 'isEqual'
  ) {
    filtersArray.push({
      tag: `Expereince: ${data.totalYearsExperienceStartRange} Year`,
      id: 'totalYearsExperienceStartRange',
    });
  } else if (
    data.totalYearsExperienceEndRange > 0 &&
    data.experienceComparisonOperator === 'isEqual'
  ) {
    filtersArray.push({
      tag: `Expereince: ${data.totalYearsExperienceEndRange} Year`,
      id: 'totalYearsExperienceEndRange',
    });
  }
  return filtersArray;
};

export const pushTalentPoolFilters = (data: TalentPoolAdvanceFilterFormProps) => {
  let filtersArray: IFilterList[] = [];
  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
  if (data.commPreference?.length)
    filtersArray.push({
      tag: `Comm. Preference: ${data.commPreference.map((obj) => obj).join(',')}`,
      id: 'commPreference',
    });

  if (data.contactAvailability?.length)
    filtersArray.push({
      tag: `Contact Avail. :${data.contactAvailability.map((obj) => obj).join(',')}`,
      id: 'contactAvailability',
    });
  if (data.currentEmployers?.length)
    filtersArray.push({
      tag: `Current Employers: ${data.currentEmployers.join(',')}`,
      id: 'currentEmployers',
    });
  if (data.workAuthorization?.length)
    filtersArray.push({
      tag: `Work Auth: ${data.workAuthorization.map((obj) => obj).join(',')}`,
      id: 'workAuthorization',
    });
  if (data.jobPreference?.length)
    filtersArray.push({
      tag: `Job Pref.:${data.jobPreference.map((obj) => obj).join(',')}`,
      id: 'jobPreference',
    });
  if (data.jobTitles?.length)
    filtersArray.push({ tag: `Job Titles: ${data.jobTitles}`, id: 'jobTitles' });
  if (data.jobType?.length) filtersArray.push({ tag: `Job Type: ${data.jobType}`, id: 'jobType' });
  if (data.languages && data.languages?.length)
    filtersArray.push({ tag: `Languages: ${data.languages}`, id: 'languages' });
  if (data.previousEmployers && data.previousEmployers?.length)
    filtersArray.push({
      tag: `Previous Employers: ${data.previousEmployers.join(',')}`,
      id: 'previousEmployers',
    });
  if (data.source?.length)
    filtersArray.push({ tag: `Source: ${data.source.join(',')}`, id: 'source' });
  if (data.tags?.length) filtersArray.push({ tag: `Tags: ${data.tags.join(',')}`, id: 'tags' });
  if (data.createBWDates?.toDateName)
    filtersArray.push({
      tag: `Created b/w: ${
        data?.createBWDates.fromDateName
          ? formattedDate(data.createBWDates?.fromDateName.toString())
          : ''
      } to ${
        data?.createBWDates.toDateName
          ? formattedDate(data.createBWDates?.toDateName.toString())
          : ''
      }`,
      id: 'createBWDates',
    });
  if (data.salaryRange?.currency)
    filtersArray.push({
      tag: `${data.salaryRange.min ?? ''} to ${data.salaryRange.max ?? ''} ${
        data.salaryRange.currency ?? ''
      } ${data.salaryRange.unitTime ?? ''}`,
      id: 'salaryRange',
    });
  if (data.candidateStatus?.length)
    filtersArray.push({
      tag: `Candidate Status: ${data.candidateStatus}`,
      id: 'candidateStatus',
    });
  if (data.recruiters?.length)
    filtersArray.push({
      tag: `Recruiter: ${data.recruiters}`,
      id: 'recruiter',
    });
  if (data.teams?.length)
    filtersArray.push({
      tag: `Team: ${data.teams}`,
      id: 'team',
    });
  if (data.noticePeriod && data.noticePeriod?.max && data.noticePeriod?.min)
    filtersArray.push({
      tag: `Notice Period: ${data.noticePeriod.min} days - ${data.noticePeriod.max} days `,
      id: 'noticePeriod',
    });
  if (data.languages?.length)
    data.languages.forEach((language) =>
      filtersArray.push({
        tag: `Languages: ${language}`,
        id: 'languages',
      })
    );

  filtersArray = addExpereinceTag(data, filtersArray);
  return filtersArray;
};

export const pushMappingFilters = (data: MappingsAdvanceFilterFormProps) => {
  let filtersArray: IFilterList[] = [];
  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
  if (data.accountManagers?.length)
    filtersArray.push({
      tag: `Acc. Managers: ${data.accountManagers.join(',')}`,
      id: 'accountManagers',
    });

  if (data.clientRecruiters?.length)
    filtersArray.push({
      tag: `Client Recruiters: ${data.clientRecruiters.join(',')}`,
      id: 'clientRecruiters',
    });
  if (data.contactAvailability?.length)
    filtersArray.push({
      tag: `Contact: ${data.contactAvailability.join(',')}`,
      id: 'contactAvailability',
    });
  if (data.clients?.length)
    filtersArray.push({ tag: `Clients: ${data.clients.join(',')}`, id: 'clients' });
  if (data.candidateStatus?.length)
    filtersArray.push({
      tag: `Candidate Status: ${data.candidateStatus.join(',')}`,
      id: 'candidateStatus',
    });
  if (data.recruiters?.length)
    filtersArray.push({ tag: `Recruiters: ${data.recruiters.join(',')}`, id: 'recruiters' });
  if (data.teams?.length)
    filtersArray.push({
      tag: `Teams: ${data.teams.map((obj) => obj.name).join(',')}`,
      id: 'teams',
    });
  if (data.createBWDates?.toDateName)
    filtersArray.push({
      tag: `Created b/w: ${
        data?.createBWDates.fromDateName
          ? formattedDate(data.createBWDates?.fromDateName.toString())
          : ''
      } to ${
        data?.createBWDates.toDateName
          ? formattedDate(data.createBWDates?.toDateName.toString())
          : ''
      }`,
      id: 'createBWDates',
    });

  if (data.noticePeriod && data.noticePeriod?.max && data.noticePeriod?.min)
    filtersArray.push({
      tag: `Notice Period: ${data.noticePeriod.min} days - ${data.noticePeriod.max} days `,
      id: 'noticePeriod',
    });

  if (data.workAuthorization?.length)
    filtersArray.push({
      tag: `Work Authorization: ${data.workAuthorization?.toString()} `,
      id: 'workAuthorization',
    });

  if (data.jobTitles?.length)
    filtersArray.push({
      tag: `Job Titles: ${data.jobTitles?.toString()} `,
      id: 'jobTitles',
    });

  if (data.source?.length)
    filtersArray.push({
      tag: `Source: ${data.source?.toString()} `,
      id: 'source',
    });

  if (data.commPreferences?.length)
    filtersArray.push({
      tag: `Comm. Preference: ${data.commPreferences.map((obj) => obj).join(',')}`,
      id: 'commPreference',
    });

  if (data.currentEmployers?.length)
    filtersArray.push({
      tag: `Current Employers: ${data.currentEmployers.join(',')}`,
      id: 'currentEmployers',
    });

  if (data.previousEmployers && data.previousEmployers?.length)
    filtersArray.push({
      tag: `Previous Employers: ${data.previousEmployers.join(',')}`,
      id: 'previousEmployers',
    });

  if (data.salaryRange?.currency)
    filtersArray.push({
      tag: `${data.salaryRange.min ?? ''} to ${data.salaryRange.max ?? ''} ${
        data.salaryRange.currency ?? ''
      } ${data.salaryRange.unitTime ?? ''}`,
      id: 'salaryRange',
    });

  if (data.languages)
    data.languages.forEach((language) =>
      filtersArray.push({
        tag: `Languages: ${language}`,
        id: 'languages',
      })
    );

  if (data.jobType) filtersArray.push({ tag: `Job Type: ${data.jobType}`, id: 'jobType' });

  if (data.jobPreference?.length)
    filtersArray.push({
      tag: `Job Pref.:${data.jobPreference.map((obj) => obj).join(',')}`,
      id: 'jobPreference',
    });

  if (data.tags?.length) filtersArray.push({ tag: `Tags: ${data.tags.join(',')}`, id: 'tags' });
  filtersArray = addExpereinceTag(data, filtersArray);
  return filtersArray;
};

export const getCandidateScoreStatus = (
  score: number,
  max: number,
  isPipeline: boolean,
  isAIMatchingPage = false,
  showTooltip = false
) => {
  const decimalPlacesForScore = isAIMatchingPage ? 2 : 1;
  const result = isPipeline
    ? parseFloat(((score / max) * 100).toFixed(2))
    : parseFloat(score.toFixed(decimalPlacesForScore));

  const matchScoreContent = (
    <Box
      height={30}
      mt={0.5}
      textAlign="center"
      p={0.5}
      borderRadius={2}
      bgcolor={
        result >= 70 ? WsColors.TheBlues100 : result >= 50 ? WsColors.Yellow100 : WsColors.Red100
      }
    >
      <WsTypography
        variant={WsTypoVariants.BODY1}
        color={
          result >= 70
            ? WsTypoColors.PRIMARY
            : result >= 50
            ? WsTypoColors.YELLOW
            : WsTypoColors.ERROR
        }
      >
        {isPipeline
          ? result >= 70
            ? 'Good Match'
            : result >= 50
            ? 'Average Match'
            : 'Low Match'
          : `${result}%`}
      </WsTypography>
    </Box>
  );
  return showTooltip ? (
    <Tooltip title="Match Score">{matchScoreContent}</Tooltip>
  ) : (
    matchScoreContent
  );
};

export const getLocation = (location: ILocation) =>
  [location.city, location.state, location.country].filter((x) => x).toString();
export const phoneOptions = [
  {
    label: 'Send SMS',
    onClick: () => {},
    icon: <TextsmsIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
];

export const emailOptions = [
  {
    label: 'Send Email',
    id: '0',
    onClick: () => {},
    icon: <MailOutlineIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    id: '1',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
];

export const getSubmissionCardDetails = (candidate: ICandidate): WsDetailsObj[] => {
  const array: WsDetailsObj[] = [];
  array.push({
    icon: <IoBarcodeOutline color={WsColors.TertiaryShades700} />,
    labelName: candidate.jobCode ?? 'Not Available',
    popOverText: 'Wisestep JobCode',
  });

  array.push({
    icon: <IoBarcodeOutline color={WsColors.TertiaryShades700} />,
    labelName: candidate.clientJobCode ?? 'Not Available',
    popOverText: 'Client JobCode',
  });

  array.push({
    icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
    labelName: candidate.jobClientRecruiterName ?? 'Not Available',
    popOverText: 'Client SPOC',
  });

  array.push({
    icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
    labelName: candidate.jobAccountManagerName ?? 'Not Available',
    popOverText: 'Account ManagerName',
  });
  if (!isEmpty(candidate.jobLocations)) {
    array.push({
      icon: <WsIconLocation />,
      labelName: getLocation(candidate?.jobLocations?.[0] as ILocation) ?? `Not Available`,
      popOverText: 'Job Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      labelName: `Not Available`,
      popOverText: 'Candidate Location',
    });
  }
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      labelName: getLocation(candidate.currentLocation) ?? 'Not Available',
      popOverText: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      labelName: `Not Available`,
      popOverText: 'Candidate Location',
    });
  }

  array.push({
    icon: <WsIconDice />,
    labelName: candidate.source ?? `Not Available`,
    popOverText: 'Source',
  });

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    labelName: candidate.relocationPreference ?? 'Not Available',
    popOverText: 'Relocation Preferences',
  });

  return array;
};

export const SmallBoxWithRadius = styled(Box)(() => ({
  border: '1px solid ',
  borderColor: WsColors.TheBlues100,
  backgroundColor: WsColors.TheBlues100,
  borderRadius: 12,
  paddingLeft: 8,
  paddingRight: 8,
  height: 25,
  alignItems: 'center',
}));

export const getInteraction = (interaction: ICandidateRecentConversation[], email: string) => {
  const currentCandidate = interaction?.find(
    (item) => item.candidateEntity.email == email
  )?.recentInteractions;
  return currentCandidate?.find((item) => item.applicationStage && item.applicationState);
};

export const setDefaultAdvanceFilters = (
  query: IJobCandidateInteraction
): SubmissionAdvancedFilterFormProps => {
  return {
    jobTitles: query.jobTitles ?? [],
    tags: query.tags ?? [],
    relocationPreference: query.relocationPreference ?? null,
    clientNames: (query.clientNames as string[]) ?? [],
    jobType: query.jobType ?? null,
    candidateLocations: (query.candidateLocations as ILocation[]) ?? [],
    jobLocations: (query.jobLocations as ILocation[]) ?? [],
    submittedByRecruiterIds: query.submittedByRecruiterIds ?? [],
    clientRecruiters: query.jobClientRecruiterEmails ?? [],
    submittedByRecruiterAutocompleteSelects: query.submittedByRecruiterIds ?? [],
    teamNameAutocompleteSelects: [],
    submissionsToJobsPostedByTeamMemberAutocompleteSelects: query.submittedAsTeamMemberEmails ?? [],
    submissionsToJobsPostedByTeamNameAutocompleteSelects: [],
    submittedBetweenFromDateTime: null,
    submittedBetweenToDateTime: null,
    candidateNotesLatestCreatedAtFromDate: query.candidateNotesLatestCreatedAtFromDate ?? null,
    candidateNotesLatestCreatedAtToDate: query.candidateNotesLatestCreatedAtToDate ?? null,
    jobStatus: query.candidateJobStatus ?? null,
    searchWithMostRecentConversation: query.searchWithMostRecentConversation,
    applicationStage: query.applicationStage ?? null,
    applicationStatus: query.applicationState ?? null,
    stageStatus: query.currentStageCurrentStatusPairs ?? [],
    salaryRange: {
      currency: query.salaryCurrency ?? null,
      unitTime: query.salaryTimeSpan ?? null,
      max: query.maxSalary ?? 0,
      min: query.minSalary ?? 0,
    },
    tat: {
      min: query.timeToServiceBetween?.from ?? null,
      max: query.timeToServiceBetween?.to ?? null,
    },
    datesUnionBy: query.datesUnionBy ?? 'CREATED_OR_UPDATED_BETWEEN',
  };
};

export const setDefaultCandidateAdvanceFilters = (
  query: IJobCandidateInteraction
): MappingsAdvanceFilterFormProps | TalentPoolAdvanceFilterFormProps => {
  return {
    jobStatus: query.jobStatus,
    createBWDates: {
      title: '',
      fromDateName: (query.fromDateTime as Date) ?? null,
      toDateName: (query.toDateTime as Date) ?? null,
    },
    accountManagers: query.jobAccountManagerEmails ?? [],
    clientRecruiters: query.clientRecruiters ?? [],
    recruiters: query.submittedByRecruiterIds ?? [],
    clients: (query.clientNames as string[]) ?? [],
    stageStatus: query.currentStageCurrentStatusPairs ?? [],
    teams: [],
    candidateStatus: query.candidateSearchTypeList ?? [],
    recruiterActions: query.recruiterActions ?? [],
    candidateResponses: query.candidateResponses ?? [],
    contactAvailability: contactAvailabilityFilterValues(
      query.emailAvailability ?? false,
      query.phoneAvailability ?? false
    ),
    jobTitles: query.jobTitles ?? [],
    workAuthorization: query.workAuthorizationCategories ?? [],
    currentEmployers: query.currentEmployers ?? [],
    previousEmployers: query.employers ?? [],
    salaryRange: {
      currency: query.salaryCurrency ?? null,
      unitTime: query.salaryTimeSpan ?? null,
      max: query.maxSalary ?? 0,
      min: query.minSalary ?? 0,
    },
    noticePeriod: {
      min: query.noticePeriodMinDays ?? 0,
      max: query.noticePeriodMaxDays ?? 0,
    },
    jobType: query.jobType ?? null,
    languages: query.languages ?? [],
    jobPreference: jobPreferenceFilterValues(
      query.jobPreferenceContract ?? false,
      query.jobPreferencePermanent ?? false
    ),
    tags: query.tags ?? [],
    source: query.sources ?? [],
    totalYearsExperienceStartRange: query?.totalYearsExperienceStartRange ?? 0,
    totalYearsExperienceEndRange: query?.totalYearsExperienceEndRange ?? 0,
    experienceComparisonOperator: query?.experienceComparisonOperator ?? 'isBetween',
    searchWithMostRecentConversation: query?.searchWithMostRecentConversation ?? false,
  };
};
export const contactAvailabilityFilterValues = (email: boolean, phone: boolean) => {
  if (email && phone) {
    return ['Email', 'Phone', 'Both'];
  } else if (email) {
    return ['Email'];
  } else if (phone) {
    return ['Phone'];
  } else {
    return [];
  }
};
export const jobPreferenceFilterValues = (contract: boolean, permanent: boolean) => {
  if (contract && permanent) {
    return ['Contract', 'Permanent'];
  } else if (contract) {
    return ['Contract'];
  } else if (permanent) {
    return ['Permanent'];
  } else {
    return [];
  }
};

export const setDefaultCandidateSearchFilters = (
  query: IJobCandidateInteraction
): TalentPoolSearchRequest => {
  return {
    locations: query.candidateAllLocationFields ?? [],
    distance: query.radiusInMiles ?? 0,
    query: query.query ?? '',
    sort: query?.sort?.[0] ?? '',
    candidateLocationPref: getLocationPreference(
      query.searchInCurrentLocation ?? false,
      query.searchInPreferredLocation ?? false
    ),
    excludeDuplicateRecords: '',
    recruiterActions: query.recruiterActions,
    candidateResponses: query.candidateResponses,
    totalYearsExperienceStartRange: query?.totalYearsExperienceStartRange ?? 0,
    totalYearsExperienceEndRange: query?.totalYearsExperienceEndRange ?? 0,
    experienceComparisonOperator: query?.experienceComparisonOperator ?? 'isBetween',
    searchScope: query.searchScope ?? 'EVERYTHING',
    searchWithMostRecentConversation: query.searchWithMostRecentConversation ?? true,
    specificSearch: query.specificSearch ?? false,
  };
};

export const getLocationPreference = (current: boolean, preferred: boolean) => {
  if (current) {
    return 'Current';
  } else if (preferred) {
    return 'Preferred';
  } else {
    return 'Current or Preferred';
  }
};

export const firingAmplitudeEvents = (
  name: string,
  clickedOnName: string,
  workspace: IWorkspaceContext,
  pageName: string
) =>
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: pageName,
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });

export const getCountryByLocation = (locations: ILocation[]) => {
  const countryLocations: ILocation[] = [];
  locations?.length > 0 &&
    locations.forEach((location) => {
      const countryLocation = {
        country: location.country,
        countryCode: location.countryCode,
        city: '',
        continent: '',
        continentCode: '',
        state: '',
        stateCode: '',
        point: location.point,
      };
      countryLocations.push(countryLocation);
    });
  return countryLocations;
};

export const getAllAlternateNumbers = (data: string[]) => {
  if (!isEmpty(data) && data.includes('false')) {
    return data.filter((val) => val != 'false');
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicates = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return data.filter((val) => val != value);
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicatesPhNos = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return getAllAlternateNumbers(data).filter(
      (val) => !getRawPhoneNumber(val).includes(getRawPhoneNumber(value))
    );
  }
  return isEmpty(data) ? [] : data;
};

const getPreferredLocation = (candidate: ICandidate): string => {
  let preferredLocation = '';
  `${
    candidate?.preferredLocations &&
    candidate?.preferredLocations?.map(
      (location) =>
        (preferredLocation =
          preferredLocation +
          `${location.city ?? ''}  ${location.state ?? ''} ${location.country ?? ''}, `)
    )
  }`;
  return preferredLocation ? preferredLocation : 'Not Available';
};
export const syncPostgreAndESCandidate = (candidate1: ICandidate, candidate2: ICandidate) => {
  candidate1['age'] = !isEmpty(candidate1['age']) ? candidate1['age'] : candidate2['age'];
  candidate1['workAuthorization'] = !isEmpty(candidate1['workAuthorization'])
    ? candidate1['workAuthorization']
    : candidate2['workAuthorization'];
  candidate1['workAuthorizationCategory'] = !isEmpty(candidate1['workAuthorizationCategory'])
    ? candidate1['workAuthorizationCategory']
    : candidate2['workAuthorizationCategory'];
  candidate1['relocationPreference'] = !isEmpty(candidate1['relocationPreference'])
    ? candidate1['relocationPreference']
    : candidate2['relocationPreference'];
  candidate1['likelyToMove'] = !isEmpty(candidate1['likelyToMove'])
    ? candidate1['likelyToMove']
    : candidate2['likelyToMove'];
  candidate1['currentJobTitle'] = !isEmpty(candidate1['currentJobTitle'])
    ? candidate1['currentJobTitle']
    : candidate2['currentJobTitle'];
  candidate1['passportNumber'] = !isEmpty(candidate1['passportNumber'])
    ? candidate1['passportNumber']
    : candidate2['passportNumber'];
  candidate1['passportType'] = !isEmpty(candidate1['passportType'])
    ? candidate1['passportType']
    : candidate2['passportType'];
  candidate1['dob'] = !isEmpty(candidate1['dob']) ? candidate1['dob'] : candidate2['dob'];
  candidate1['gender'] = !isEmpty(candidate1['gender'])
    ? candidate1['gender']
    : candidate2['gender'];
  candidate1['age'] = !isEmpty(candidate1['age']) ? candidate1['age'] : candidate2['age'];
  candidate1['postalAddress'] = !isEmpty(candidate1['postalAddress'])
    ? candidate1['postalAddress']
    : candidate2['postalAddress'];
  candidate1['ssnNumber'] = !isEmpty(candidate1['ssnNumber'])
    ? candidate1['ssnNumber']
    : candidate2['ssnNumber'];
  candidate1['panCard'] = !isEmpty(candidate1['panCard'])
    ? candidate1['panCard']
    : candidate2['panCard'];
  candidate1['aadharNumber'] = !isEmpty(candidate1['aadharNumber'])
    ? candidate1['aadharNumber']
    : candidate2['aadharNumber'];
  candidate1['email'] = !isEmpty(candidate1['email']) ? candidate1['email'] : candidate2['email'];
  candidate1['name'] = !isEmpty(candidate1['name']) ? candidate1['name'] : candidate2['name'];
  candidate1['phoneNumber'] = !isEmpty(candidate1['phoneNumber'])
    ? candidate1['phoneNumber']
    : candidate2['phoneNumber'];
  candidate1['alternateEmails'] = !isEmpty(candidate1['alternateEmails'])
    ? candidate1['alternateEmails']
    : candidate2['alternateEmails'];
  candidate1['alternatePhoneNumbers'] = !isEmpty(candidate1['alternatePhoneNumbers'])
    ? candidate1['alternatePhoneNumbers']
    : candidate2['alternatePhoneNumbers'];
  candidate1['profileURLs'] = !isEmpty(candidate1['profileURLs'])
    ? candidate1['profileURLs']
    : candidate2['profileURLs'];
  candidate1['candidateId'] = !isEmpty(candidate1['candidateId'])
    ? candidate1['candidateId']
    : candidate2['candidateId'];
  candidate1['candidateLanguages'] = !isEmpty(candidate1['candidateLanguages'])
    ? candidate1['candidateLanguages']
    : candidate2['candidateLanguages'];
};

export const syncCandidateObjects = (candidate1: ICandidate, candidate2: ICandidate) => {
  candidate1['email'] = !isEmpty(candidate1['email']) ? candidate1['email'] : candidate2['email'];
  candidate1['name'] = !isEmpty(candidate1['name']) ? candidate1['name'] : candidate2['name'];
  candidate1['phoneNumber'] = !isEmpty(candidate1['phoneNumber'])
    ? candidate1['phoneNumber']
    : candidate2['phoneNumber'];
  candidate1['alternateEmails'] = !isEmpty(candidate1['alternateEmails'])
    ? candidate1['alternateEmails']
    : candidate2['alternateEmails'];
  candidate1['alternatePhoneNumbers'] = !isEmpty(candidate1['alternatePhoneNumbers'])
    ? candidate1['alternatePhoneNumbers']
    : candidate2['alternatePhoneNumbers'];
  candidate1['profileURLs'] = !isEmpty(candidate1['profileURLs'])
    ? candidate1['profileURLs']
    : candidate2['profileURLs'];
  candidate1['candidateId'] = !isEmpty(candidate1['candidateId'])
    ? candidate1['candidateId']
    : candidate2['candidateId'];
  candidate1['candidateLanguages'] = !isEmpty(candidate1['candidateLanguages'])
    ? candidate1['candidateLanguages']
    : candidate2['candidateLanguages'];
  candidate1['noticePeriodInDays'] = !isEmpty(candidate1['noticePeriodInDays'])
    ? candidate1['noticePeriodInDays']
    : candidate2['noticePeriodInDays'];
};

export const getIdByEmail = async (email, recruiter?: IUserDTO, isOpenNewTab = true) => {
  const user = (await getUserByEmail(email))?.data;
  const currentUserTeams = await getOverlapTeams(email);
  const hasOverlap = currentUserTeams.data?.length > 0 ? true : false;
  const url = `/recruiter/profile?id=${String(user.id)}&email=${email}&isLink=true&isOverLap=${
    hasOverlap ? 'yes' : 'no'
  }`;
  isOpenNewTab ? openUrlInNewTab(url) : (window.location.href = url);
};

export const getRecruiterAnalyticsUrl = async (email: string) => {
  try {
    const user = (await getUserByEmail(email))?.data;
    const currentUserTeams = await getOverlapTeams(email);
    const hasOverlap = currentUserTeams.data?.length > 0 ? true : false;
    const url = `/recruiter/profile?id=${user.id}&email=${email}&isLink=true&isOverLap=${
      hasOverlap ? 'yes' : 'no'
    }`;
    return url;
  } catch (error) {
    console.log('Error gettting analytics url', error);
  }
};

export const getUserAnalyticsUrl = async (email: string) => {
  try {
    const user = (await getUserByEmail(email))?.data;
    const currentUserTeams = await getOverlapTeams(email);
    const hasOverlap = !!currentUserTeams.data?.length;
    const url = `/users/details-page?id=${user.id}&email=${email}&isLink=true&isOverLap=${
      hasOverlap ? 'yes' : 'no'
    }&isUserAnalytics=true&goBackUrl=/workspace-users-and-teams/users-list`;
    return url;
  } catch (error) {
    console.error('Error gettting analytics url', error);
  }
};

export const getAccountManagerAnalyticsUrl = async (email: string) => {
  try {
    const accountManager = (await getUserByEmail(email))?.data;
    const currentUserTeams = await getOverlapTeams(email);
    const hasOverlap = currentUserTeams?.data?.length > 0 ? true : false;
    const url = `/account-manager/details-page?id=${email}&name=${
      accountManager?.firstName
    }&isOverLap=${hasOverlap ? 'yes' : 'no'}`;
    return url;
  } catch (error) {
    console.log('Error getting analytics url', error);
  }
};
export const navigateToClientAnalytics = async (
  navigate: NavigateFunction,
  job: IJob,
  isAuthorizedViewClientListing: boolean | '' | undefined,
  isAuthorizedViewAnalytics: boolean | '' | undefined,
  setNotification: (notification: INotification) => void
) => {
  try {
    if (!isAuthorizedViewClientListing || !isAuthorizedViewAnalytics) {
      setNotification(warning('You do not have access to view the client data'));
      return;
    }
    job &&
      navigate(
        `/client-details?tabIndex=0&isChild=true&name=${job?.companyName}&jobCode=${
          job?.jobCode
        }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`
      );
  } catch (error) {
    console.error('error fetching data', error);
  }
};

export const navigateToClientInfo = async (navigate: NavigateFunction, candidate: ICandidate) => {
  try {
    const jobResponse = candidate.jobCode && (await getJobByJobCode(candidate.jobCode));
    jobResponse &&
      navigate(
        `/client-details?tabIndex=0&isChild=true&name=${jobResponse?.data?.companyName}&jobCode=${
          jobResponse?.data?.jobCode
        }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`
      );
  } catch (error) {
    console.error('error fetching data', error);
  }
};

export const SmallBoxWithBgColor = styled(SmallBoxWithRadius)(() => ({
  background: WsColors.TheBlues400,
  borderRadius: 4,
  border: '0px',
}));
export interface parsedCandidatesType {
  fileUrl: string;
  fileName: string;
  isParsed: boolean;
  candidate: any;
}

export const StyledButton = styled(Button)({
  border: WsColors.Red500,
  color: WsColors.White,
  textTransform: 'none',
  fontWeight: 'bold',
  backgroundColor: WsColors.Red400,
  ':hover': {
    backgroundColor: WsColors.Red500,
    border: WsColors.Red500,
  },
});

export type ResumeDetails = {
  url: string;
  fileName: string;
  resumeUpdatedOn: string;
  s3FileName?: string;
};

export const getCandidateRTRStatusData = (rtrDataObj: string) => {
  const statusMap = ['RTR Expired', 'RTR Accepted', 'RTR Rejected', 'RTR Requested'];
  return statusMap.find((status) => rtrDataObj.includes(status)) || '';
};

export const jobBoardsList: IJobBoard[] = [
  {
    name: 'Dice',
    icon: <img src={Dice} alt="Dice" height={24} width={24} />,
    url: '/native-search-new/dice',
  },
  {
    name: 'Tech Fetch',

    icon: <img src={TechFetchIcon} alt="Tech Fetch Icon" height={24} width={24} />,
    url: '/native-search-new/techfetch',
  },
  {
    name: 'Monster US & CA',
    icon: <img src={MonsterIcon} alt="Tech Fetch Icon" height={24} width={24} />,
    url: '/native-search-new/monster?loc=us,canada',
  },
  {
    name: 'Career Builder',

    icon: <img src={CareerBuilder} alt="Career Builder" height={24} width={24} />,
    url: '/native-search-new/careerbuilder',
  },
  {
    name: 'CareerBuilder Edge',

    icon: <img src={CareerBuilder} alt="CareerBuilder Edge" height={24} width={24} />,
    url: '/native-search-new/careerbuilderedge',
  },
  {
    name: 'CV Library',

    icon: <img src={cv} alt="CVLibraryIcon" height={24} width={24} />,
    url: '/native-search-new/cvlibrary',
  },
  {
    name: 'Job Serve',

    icon: <img src={JobServe} alt="JobServeIcon" height={24} width={24} />,
    url: '/native-search-new/jobServe',
  },
  {
    name: 'Job Site',

    icon: <img src={JobisiteNew} alt="JobisiteIcon" height={24} width={24} />,
    url: '/native-search-new/jobsite',
  },
  {
    name: 'CW Jobs',

    icon: <img src={CWJob} alt="CWJobsIcon" height={24} width={24} />,
    url: '/native-search-new/cwJobs',
  },
  {
    name: 'Monster',

    icon: <img src={MonsterIcon} alt="Tech Fetch Icon" height={24} width={24} />,
    url: '/native-search-new/monster?loc=europe,uk',
  },
];

export type JobBoardRouteOptions =
  | 'dice'
  | 'techfetch'
  | 'monster'
  | 'careerbuilder'
  | 'careerbuilderedge'
  | 'cvlibrary'
  | 'jobServe'
  | 'jobsite'
  | 'cwJobs';

export const jobBoardRouteName: JobBoardRouteOptions[] = [
  'dice',
  'techfetch',
  'monster',
  'careerbuilder',
  'careerbuilderedge',
  'cvlibrary',
  'jobServe',
  'jobsite',
  'cwJobs',
];

export const generateBackwardURL = (label): IBreadCrumbsList[] => {
  const searchParam = new URLSearchParams(window.location.search);
  if (!searchParam.has('redirection')) {
    searchParam.append('redirection', 'true');
  }
  const finalUrl = `${window.location.pathname}?${searchParam.toString()}`;

  const backwardpathArray: IBreadCrumbsList[] = [
    {
      url: finalUrl,
      label: label,
    },
  ];
  return backwardpathArray;
};
